"use client";

import { useMemo } from "react";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { motion } from "framer-motion";
import { format } from "date-fns";
import Image from "next/image";
import {
  ArrowBack,
  Email,
  Facebook,
  Twitter,
  LinkedIn,
  ArrowForward,
} from "@mui/icons-material";
import { parseComponentJsonBody } from "@/lib/general.utils";
import useGeneralStore from "@/store/General/useGeneralStore";

const BlogDetailComponent = () => {
  const { t, i18n } = useTranslation("common");
  const router = useRouter();
  const { id } = router.query;
  const {
    BlogDetailComponent: blog,
    isLoadingBlogDetailComponent,
    BlogsComponent: blogsData,
    isLoadingBlogsComponent,
  } = useGeneralStore((state) => ({
    BlogDetailComponent: state.BlogDetailComponent,
    isLoadingBlogDetailComponent: state.isLoadingBlogDetailComponent,
    BlogsComponent: state.BlogsComponent,
    isLoadingBlogsComponent: state.isLoadingBlogsComponent,
  }));

  const isLoading = isLoadingBlogDetailComponent;
  const blogs = blogsData || [];

  // Get recommended blogs (exclude current)
  const recommendedBlogs = useMemo(() => {
    if (!id || !blogs || blogs.length === 0) return [];
    const currentBlogId = parseInt(id as string);
    if (isNaN(currentBlogId)) return [];

    return blogs
      .filter((b: any) => {
        const blogId = typeof b.id === "string" ? parseInt(b.id) : b.id;
        return blogId !== currentBlogId && blogId;
      })
      .slice(0, 3);
  }, [id, blogs]);

  if (!blog) return null;

  const handleBlogClick = (blogId: number) => {
    router.push(`/blogs/${blogId}`);
  };

  const renderBlogContent = (blogData: any) => {
    if (!blogData?.jsonBody) return null;

    try {
      const parsed = parseComponentJsonBody(blogData.jsonBody);
      const html = parsed.html || "";
      if (!html) return null;

      return (
        <div
          className="prose prose-lg max-w-none blog-content"
          style={{
            direction: i18n.language === "ar" ? "rtl" : "ltr",
          }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    } catch (error) {
      console.error("Error rendering blog content:", error);
      return null;
    }
  };
  return (
    <div dir={i18n.language === "ar" ? "rtl" : "ltr"} lang={i18n.language}>
      <div className="max-w-7xl mx-auto px-[2rem] py-[4rem]">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Left Sidebar */}
          <aside className="lg:col-span-1">
            {/* Back Button */}
            <button
              onClick={() => router.push("/blogs")}
              className="flex items-center gap-2 text-gray-600 hover:text-primary mb-8 transition-colors"
            >
              {i18n.language === "ar" ? <ArrowForward /> : <ArrowBack />}
              <span>{t("blogsPage.backToBlogs")}</span>
            </button>

            {/* Recommended Blogs */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t("blogsPage.relatedBlogs")}
              </h3>
              {isLoadingBlogDetailComponent || isLoadingBlogsComponent ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse">
                      <div className="w-full h-40 bg-gray-200 rounded-lg mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  ))}
                </div>
              ) : recommendedBlogs.length > 0 ? (
                <div className="space-y-6">
                  {recommendedBlogs.map((recommendedBlog) => (
                    <motion.div
                      key={recommendedBlog.id}
                      whileHover={{ scale: 1.02 }}
                      className="cursor-pointer"
                      onClick={() => handleBlogClick(recommendedBlog.id)}
                    >
                      <div className="relative w-full h-40 mb-2 rounded-lg overflow-hidden">
                        {recommendedBlog.imageUrl && (
                          <Image
                            src={
                              recommendedBlog.imageUrl ||
                              "/images/placeholder.png"
                            }
                            alt={recommendedBlog.title || ""}
                            fill
                            className="object-cover"
                          />
                        )}
                        {!recommendedBlog.imageUrl && (
                          <div className="w-full h-40 bg-gray-200 rounded-lg mb-2"></div>
                        )}
                      </div>
                      <h4 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-1">
                        {recommendedBlog.title || ""}
                      </h4>
                      <p className="text-xs text-gray-500">
                        {recommendedBlog.createdAt
                          ? format(
                              new Date(recommendedBlog.createdAt),
                              "MMM dd, yyyy",
                            )
                          : ""}
                      </p>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  {t("blogsPage.noRelatedBlogs")}
                </p>
              )}
            </div>
          </aside>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Blog Image */}
              {blog.imageUrl && (
                <div className="relative w-full h-[30rem] mb-8 rounded-lg overflow-hidden">
                  <Image
                    src={blog.imageUrl || ""}
                    alt={blog.title || ""}
                    fill
                    className="object-cover"
                  />
                </div>
              )}

              {/* Blog Title */}
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {blog.title || ""}
              </h1>

              {/* Blog Meta */}
              {blog.createdAt && (
                <div className="flex items-center gap-4 mb-6 text-sm text-gray-600">
                  <span>
                    {t("blogsPage.publishedOn")}{" "}
                    {blog.createdAt
                      ? format(new Date(blog.createdAt), "MMMM dd, yyyy")
                      : ""}
                  </span>
                </div>
              )}
              {/* Social Sharing */}
              <div className="flex items-center gap-4 mb-8 pb-6 border-b">
                <span className="text-sm font-medium text-gray-700">
                  {t("blogsPage.shareOn")}:
                </span>
                <div className="flex gap-3">
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                      typeof window !== "undefined" ? window.location.href : "",
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    <Facebook />
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                      typeof window !== "undefined" ? window.location.href : "",
                    )}&text=${encodeURIComponent(blog.title || "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-blue-400 transition-colors"
                  >
                    <Twitter />
                  </a>
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                      typeof window !== "undefined" ? window.location.href : "",
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-blue-700 transition-colors"
                  >
                    <LinkedIn />
                  </a>
                  <a
                    href={`mailto:?subject=${encodeURIComponent(
                      blog.title || "",
                    )}&body=${encodeURIComponent(
                      typeof window !== "undefined" ? window.location.href : "",
                    )}`}
                    className="text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    <Email />
                  </a>
                </div>
              </div>

              {/* Blog Content */}
              <div className="prose prose-lg max-w-none">
                {renderBlogContent(blog)}
              </div>
            </motion.article>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogDetailComponent;
